import { useEffect } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import isEmpty from 'lodash/isEmpty'
import omitBy from 'lodash/omitBy'

import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { CycleSelector } from 'modules/engagement_surveys/components/CycleSelector'
import { type FormFiltersStorage, useFormCycleSelection } from 'modules/forms'

import { InsightsVisualization } from './InsightsVisualization'
import { useFormInsightsDataSource } from './useFormInsightsDataSource'

interface Props {
  formId: string
  resourceType: string
  persistFilters?: boolean
  survey?: {
    form?: {
      schedule?: {
        cycles?: Array<{
          id: string
          startsAt: string
          endsAt?: string
        }>
      } | null
      materializedForms?: Array<{
        id: string
        startsAt?: string | null
        endsAt?: string | null
      }>
    }
  } | null
  headerContent?: (
    activeFilters: FormFiltersStorage['filters'],
    materializedFormIds?: string[]
  ) => React.ReactNode
}

export function FormInsightsOverview({
  formId,
  resourceType,
  persistFilters = false,
  survey,
  headerContent,
}: Props) {
  const [persistedFilters, setPersistedFilters] = useLocalStorage<FormFiltersStorage | undefined>(
    LOCAL_STORAGE_KEYS.formResponseFilters(formId)
  )

  const { cycles, selectedCycleId, handleCycleChange, hasCycles } = useFormCycleSelection(
    survey,
    persistedFilters ?? undefined
  )

  const source = useFormInsightsDataSource({
    formId,
    resourceType,
    currentFilters: omitBy(persistedFilters?.filters, isEmpty),
  })

  const currentFiltersKey = JSON.stringify(source.currentFilters)

  useEffect(() => {
    if (persistFilters) {
      setPersistedFilters({ filters: source.currentFilters, cycleId: selectedCycleId ?? undefined })
    }
  }, [persistFilters, selectedCycleId, setPersistedFilters, currentFiltersKey])

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateRows: 'auto',
        gridTemplateColumns: '1fr',
      }}
    >
      <Box style={{ gridRow: '1', gridColumn: '1' }}>
        <OneDataCollection
          source={source}
          visualizations={[
            {
              type: 'custom',
              label: '',
              icon: Plus,
              component: (props) => (
                <InsightsVisualization
                  {...props}
                  source={source}
                  headerContent={
                    headerContent ? (
                      <Box width="full" alignItems="center" paddingTop="s16" paddingBottom="s16">
                        <Box maxWidth="narrowPage" width="full" paddingX="s16">
                          {headerContent(
                            source.currentFilters,
                            selectedCycleId ? [selectedCycleId] : undefined
                          )}
                        </Box>
                      </Box>
                    ) : undefined
                  }
                  materializedFormId={selectedCycleId ?? undefined}
                />
              ),
            },
          ]}
        />
      </Box>

      {hasCycles && (
        <Box
          style={{
            gridRow: '1',
            gridColumn: '1',
            alignSelf: 'start',
            justifySelf: 'end',
            pointerEvents: 'none',
          }}
          paddingRight="s16"
        >
          <Box style={{ pointerEvents: 'auto' }}>
            <CycleSelector
              cycles={cycles}
              selectedCycleId={selectedCycleId}
              onCycleChange={handleCycleChange}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export type { FormFiltersStorage }
